<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']">
            <UCard :ui="{ rounded: 'rounded-lg' }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            Add {{ snakeToTitle(namespace || '') }}
                        </h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="label" label="Label">
                        <UInput v-model="state.label" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="description" label="Description">
                        <UInput v-model="state.description" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup v-if="namespace === OptionNamespace.diagnosis" name="priority" label="Priority">
                        <USelect v-model="state.priority" size="xl" :options="priorities" />
                    </UFormGroup>
                    <UFormGroup v-if="namespace === OptionNamespace.callDecision" name="send_admission_message">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-700">Send Admission Message</p>
                                <p class="text-xs text-gray-500">Automatically send an admission message to the customer when this decision is selected.</p>
                            </div>
                            <UToggle v-model="state.send_admission_message" color="primary" />
                        </div>
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </UModal>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import { useString } from '~/composables/useString'
import { OptionNamespace } from '~/types/option'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    namespace?: OptionNamespace
}>()

const priorities = [
    { value: '1', label: 'P1' },
    { value: '2', label: 'P2' },
    { value: '3', label: 'P3' },
    { value: '4', label: 'P4' },
    { value: '5', label: 'P5' },
]

const { snakeToTitle } = useString()

const client = useSupabaseClient()

const form = ref()
const state = ref()
const loading = ref<boolean>(false)

const schema = z.object({
    label: z.string(),
    description: z.string().optional(),
    priority: z.string().optional(),
    send_admission_message: z.boolean().optional(),
})

watch(() => props.open, (open) => {
    if (open) {
        state.value = {
            label: undefined,
            description: undefined,
            priority: props.namespace === OptionNamespace.diagnosis
                ? '3'
                : undefined,
            send_admission_message: false,
        }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    if (props.namespace) {
        loading.value = true

        const metadata: Record<string, any> =
            props.namespace === OptionNamespace.diagnosis
                ? { priority: event.data.priority }
                : props.namespace === OptionNamespace.callDecision
                    ? { send_admission_message: !!event.data.send_admission_message }
                    : {}

        const { error } = await client.from('options').insert({
            namespace: props.namespace,
            label: event.data.label,
            description: event.data.description || '',
            metadata,
        } as never)

        if (error) {
            console.log(error)
        }

        dismiss(true)
        loading.value = false
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}
</script>